import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import styled from 'styled-components';
import { useDirectorLoginMutation, useDirectorForgotPasswordMutation } from '../../redux/services/director-api';

// ── Styled components (mismo sistema visual que login.js del admin) ───────────

const Root = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f4f1e9;
  font-family: 'Avenir Next', 'Trebuchet MS', sans-serif;

  @media (max-width: 640px) { padding: 16px; }
`;

const Shell = styled.section`
  width: min(100%, 1120px);
  min-height: 720px;
  display: grid;
  grid-template-columns: minmax(320px, 1.15fr) 0.85fr;
  background: #ffffff;
  border: 1px solid rgba(53, 62, 53, 0.45);
  box-shadow: 0 28px 80px rgba(38, 42, 33, 0.14);
  overflow: hidden;

  @media (max-width: 900px) { min-height: auto; grid-template-columns: 1fr; }
`;

const IllustrationPanel = styled.div`
  position: relative;
  min-height: 720px;
  background: linear-gradient(180deg, #244f33 0%, #274e35 100%);
  overflow: hidden;

  @media (max-width: 900px) { min-height: 320px; }
`;

const PanelGlow = styled.div`
  position: absolute;
  inset: auto auto -12% -12%;
  width: 58%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 72%);
`;

const Moon = styled.div`
  position: absolute;
  top: 94px; right: 72px;
  width: 78px; height: 78px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #fcffff 0%, #cad2cd 52%, #b4beb8 100%);
  box-shadow: 0 0 20px rgba(255,255,255,0.35);
  &::after {
    content: '';
    position: absolute;
    top: 2px; left: 22px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: #274e35;
  }
`;

const StarField = styled.div`
  position: absolute;
  top: 88px; left: 62px;
  width: 132px; height: 100px;
`;

const Star = styled.span`
  position: absolute;
  width: ${p => p.size || 12}px;
  height: ${p => p.size || 12}px;
  left: ${p => p.left || 0}px;
  top: ${p => p.top || 0}px;
  transform: rotate(45deg);
  &::before, &::after {
    content: '';
    position: absolute;
    inset: 0; margin: auto;
    background: rgba(241,249,244,0.95);
    border-radius: 999px;
    box-shadow: 0 0 10px rgba(255,255,255,0.85);
  }
  &::before { width: 100%; height: 2px; }
  &::after  { width: 2px; height: 100%; }
`;

const MountainWrap = styled.div`
  position: absolute;
  left: 50%; bottom: 62px;
  width: min(82%, 460px);
  transform: translateX(-50%);
`;

const Ground = styled.div`
  width: 100%; height: 4px;
  border-radius: 999px;
  background: rgba(255,255,255,0.9);
  box-shadow: 0 0 10px rgba(255,255,255,0.35);
`;

const Mountain = styled.div`
  position: relative;
  width: 76%; aspect-ratio: 1.15;
  margin: 0 auto -3px;
  clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
  background: linear-gradient(135deg, #f8fbf8 0%, #dde4df 45%, #f7faf7 100%);
  filter: drop-shadow(0 0 14px rgba(255,255,255,0.38));
  &::before {
    content: '';
    position: absolute; inset: 0;
    clip-path: polygon(0 100%, 50% 0, 50% 100%);
    background: linear-gradient(180deg, rgba(205,216,208,.95) 0%, rgba(244,248,244,.96) 72%, rgba(255,255,255,.98) 100%);
  }
  &::after {
    content: '';
    position: absolute;
    left: 31%; right: 31%; bottom: 0;
    height: 44%;
    clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
    background: #274e35;
  }
`;

const FormPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 56px;
  background: #ffffff;

  @media (max-width: 900px) { padding: 48px 28px; }
`;

const Form = styled.form`
  width: 100%;
  max-width: 360px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #284B32;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 38px;
  color: #8d8d8d;
  font-size: 0.95rem;
  text-align: center;
`;

const Alert = styled.div`
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  border: 1px solid ${p => p.success ? '#c1efd0' : '#efc3c3'};
  color: ${p => p.success ? '#1e5c30' : '#9d2f2f'};
  background: ${p => p.success ? '#f0fbf4' : '#fff3f3'};
`;

const Field = styled.label`
  position: relative;
  display: block;
  margin-bottom: 22px;
`;

const FieldLabel = styled.span`
  position: absolute;
  top: -10px; left: 16px;
  padding: 0 7px;
  color: #8b8b8b;
  background: #ffffff;
  font-size: 0.95rem;
  line-height: 1;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 56px;
  padding: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #1f1f1f;
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    border-color: #284b31;
    box-shadow: 0 0 0 3px rgba(40,75,49,0.12);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 56px;
  margin-top: 4px;
  border: 0;
  border-radius: 4px;
  background: #284b31;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  &:hover  { background: #203c27; }
  &:active { transform: translateY(1px); }
  &:disabled { background: #9ab09f; cursor: not-allowed; }
`;

const TextLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #0F6E56;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: #0a5a45; }
`;

const Footer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

// ── Componente ────────────────────────────────────────────────────────────────

const DirectorLoginPage = () => {
  const navigate = useNavigate();
  const [directorLogin, { isLoading }] = useDirectorLoginMutation();
  const [directorForgot, { isLoading: isSendingReset }] = useDirectorForgotPasswordMutation();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = await directorLogin(form).unwrap();
      localStorage.setItem('director_token', data.token);
      if (data.tempPassword) {
        navigate('/director/change-password');
      } else {
        navigate('/director/dashboard');
      }
    } catch (err) {
      setError(err?.data?.message || 'Credenciales inválidas');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    try {
      const data = await directorForgot({ email: form.email }).unwrap();
      setSuccessMsg(data.message);
    } catch {
      setSuccessMsg('Si el email existe, recibirás un enlace.');
    }
  };

  return (
    <Root>
      <Shell>
        <IllustrationPanel>
          <PanelGlow />
          <StarField aria-hidden="true">
            <Star left={0}  size={18} top={10} />
            <Star left={28} size={22} top={28} />
            <Star left={24} size={17} top={58} />
            <Star left={68} size={14} top={54} />
            <Star left={92} size={12} top={82} />
          </StarField>
          <Moon aria-hidden="true" />
          <MountainWrap aria-hidden="true">
            <Mountain />
            <Ground />
          </MountainWrap>
        </IllustrationPanel>

        <FormPanel>
          <Form onSubmit={forgotMode ? handleForgot : handleLogin}>
            <Title>{forgotMode ? 'Restablecer contraseña' : 'Portal del director'}</Title>
            <Subtitle>
              {forgotMode
                ? 'Ingresa tu email y te enviaremos un enlace'
                : 'Acceso exclusivo para directores de club'}
            </Subtitle>

            {error      && <Alert>{error}</Alert>}
            {successMsg && <Alert success>{successMsg}</Alert>}

            <Field>
              <FieldLabel>Email</FieldLabel>
              <Input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </Field>

            {!forgotMode && (
              <Field>
                <FieldLabel>Contraseña</FieldLabel>
                <Input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  required
                />
              </Field>
            )}

            <SubmitButton type="submit" disabled={isLoading || isSendingReset}>
              {forgotMode
                ? (isSendingReset ? 'Enviando...' : 'Enviar enlace')
                : (isLoading     ? 'Ingresando...' : 'Ingresar')}
            </SubmitButton>

            <Footer>
              <TextLink type="button" onClick={() => { setForgotMode(v => !v); setError(''); setSuccessMsg(''); }}>
                {forgotMode ? 'Volver al login' : 'Olvidé mi contraseña'}
              </TextLink>
            </Footer>
          </Form>
        </FormPanel>
      </Shell>
    </Root>
  );
};

export default DirectorLoginPage;
